import { useToast } from "@hightouch/htui";
import type {
  CommentAnchor,
  CommentThreadSubject,
} from "@hightouch/lib/llm/asset-comments/types";
import {
  CommentComposer,
  type CommentDraft,
} from "src/components/comments/comment-composer";
import { useCommentFile } from "src/components/comments/comment-file";
import { toSubjectInput } from "src/components/comments/subject";
import { useInvalidateCommentQueries } from "src/components/comments/use-comment-threads";
import { useMentionSearch } from "src/components/comments/use-mention-search";
import { toMentionInput } from "src/components/comments/utils";
import { useCreateAssetCommentThreadMutation } from "src/graphql";

type Props = {
  subject: CommentThreadSubject;
  brandProfileId?: string | null;
  newCommentAnchor?: CommentAnchor | null;
  autoFocus?: boolean;
};

export function NewFileComment({
  subject,
  brandProfileId,
  newCommentAnchor,
  autoFocus = true,
}: Props) {
  const { toast } = useToast();
  const file = useCommentFile();
  const searchMentions = useMentionSearch({ brandProfileId });
  const invalidateCommentQueries = useInvalidateCommentQueries();
  const createThread = useCreateAssetCommentThreadMutation({
    onSuccess: invalidateCommentQueries,
  });
  const post = async (draft: CommentDraft) => {
    try {
      await createThread.mutateAsync({
        subject: toSubjectInput(subject),
        body: draft.body,
        anchor: newCommentAnchor,
        mentions: draft.mentions.map(toMentionInput),
        ampFileId: file?.id,
      });
    } catch (error) {
      toast({
        id: "file-comment-create",
        title: "Couldn't post your comment",
        message: error instanceof Error ? error.message : undefined,
        variant: "error",
      });
    }
  };
  return (
    <CommentComposer
      placeholder="Add a comment…"
      searchMentions={searchMentions}
      autoFocus={autoFocus}
      onSubmit={(draft) => void post(draft)}
    />
  );
}
